import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Archive,
  CheckCircle,
  Download,
  Eye,
  FileArchive,
  FileSignature,
  FileText,
  History,
  LockKeyhole,
  PenLine,
  Printer,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
} from 'lucide-react';
import { useAuth } from '@/core/hooks';

type TabKey = 'library' | 'upload' | 'templates' | 'signatures' | 'logs' | 'audit' | 'permissions';

interface DocumentRecord {
  id: string;
  title: string;
  category: string;
  documentType?: string;
  ownerType?: string;
  ownerName?: string;
  status?: string;
  expiryDate?: string;
  currentVersion?: number;
  accessRoles?: string[];
  permissions?: string[];
  requiresSignature?: boolean;
  signedAt?: string;
  signedBy?: string;
  contentText?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface DocumentTemplate {
  id: string;
  title: string;
  category: string;
  description?: string;
  mergeFields?: string[];
  contentText?: string;
  defaultPermissions?: string[];
  accessRoles?: string[];
  requiresSignature?: boolean;
}

interface DocumentVersion {
  id: string;
  documentId: string;
  version: number;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  contentText?: string;
  fileDataUrl?: string;
  storageProvider?: string;
  objectKey?: string;
  objectAlgorithm?: string;
  objectChecksumSha256?: string;
  objectSizeBytes?: number;
  createdBy?: string;
  createdAt?: string;
}

interface DocumentAccessLog {
  id: string;
  documentId: string;
  action: string;
  actor: string;
  role?: string;
  at?: string;
  details?: string;
}

interface DocumentSignature {
  id: string;
  documentId: string;
  signerName: string;
  signerRole: string;
  signatureText?: string;
  certificateId?: string;
  signatureMethod?: string;
  signatureImageDataUrl?: string;
  signedAt?: string;
  status?: string;
}

interface PersonRecord {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  carePackage?: string;
  hourlyRate?: number;
  defaultHourlyRate?: number;
}

interface UploadFormState {
  title: string;
  category: string;
  documentType: string;
  ownerType: string;
  ownerName: string;
  expiryDate: string;
  contentText: string;
  fileDataUrl: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  accessRoles: string[];
  permissions: string[];
  requiresSignature: boolean;
}

interface FillFormState {
  templateId: string;
  targetType: 'carer' | 'client' | 'manual';
  targetId: string;
  valuesText: string;
}

const roleOptions = ['ADMIN', 'MANAGER', 'COORDINATOR', 'CARER', 'FAMILY'];
const permissionOptions = ['view', 'download', 'print', 'sign', 'replace-version', 'archive'];
const categories = ['Legal', 'HR', 'Client Contracts', 'Payroll', 'GDPR', 'eMAR', 'Compliance', 'Training', 'Policies', 'General'];

const emptyUploadForm: UploadFormState = {
  title: '',
  category: 'General',
  documentType: 'Document',
  ownerType: 'Company',
  ownerName: '',
  expiryDate: '',
  contentText: '',
  fileDataUrl: '',
  fileName: '',
  mimeType: 'text/plain',
  sizeBytes: 0,
  accessRoles: ['ADMIN', 'MANAGER'],
  permissions: ['view', 'download', 'print', 'sign'],
  requiresSignature: true,
};

const emptyTemplateForm: DocumentTemplate = {
  id: '',
  title: '',
  category: 'Client Contracts',
  description: '',
  mergeFields: [],
  contentText: '',
  defaultPermissions: ['view', 'download', 'print', 'sign'],
  accessRoles: ['ADMIN', 'MANAGER'],
  requiresSignature: true,
};

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getCollection<T>(collection: string, token?: string | null): Promise<T[]> {
  const response = await fetch(`/api/v1/${collection}`, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(`Could not load ${collection}`);
  const payload = await response.json() as { data?: T[] };
  return Array.isArray(payload.data) ? payload.data : [];
}

async function saveCollectionRecord<T>(collection: string, payload: Partial<T>, token?: string | null, id?: string): Promise<T> {
  const response = await fetch(`/api/v1/${collection}${id ? `/${encodeURIComponent(id)}` : ''}`, {
    method: id ? 'PATCH' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorPayload.error || `Could not save ${collection}`);
  }
  const result = await response.json() as { data?: T };
  if (!result.data) throw new Error(`Empty ${collection} response`);
  return result.data;
}

function displayName(person?: PersonRecord): string {
  if (!person) return '';
  return person.fullName || [person.firstName, person.lastName].filter(Boolean).join(' ') || person.id;
}

function parseValuesText(text: string): Record<string, string> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      return Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key, String(value ?? '')]));
    } catch {
      return {};
    }
  }
  return Object.fromEntries(
    trimmed
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separator = line.includes(':') ? ':' : '=';
        const [key, ...rest] = line.split(separator);
        return [String(key || '').trim(), rest.join(separator).trim()];
      })
      .filter(([key]) => Boolean(key))
  );
}

function parseMergeFieldsFromContent(text: string): string[] {
  const fields = new Set<string>();
  const matcher = /{{\s*([a-zA-Z0-9_.-]+)\s*}}/g;
  let match = matcher.exec(text);
  while (match) {
    fields.add(match[1]);
    match = matcher.exec(text);
  }
  return Array.from(fields);
}

function parseMergeFieldsText(text: string): string[] {
  return Array.from(new Set(
    text
      .split(/[\n,]/)
      .map((field) => field.trim())
      .filter(Boolean)
  ));
}

function templateIdFromTitle(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `document-template-${slug || Date.now()}`;
}

function fillTemplate(template: DocumentTemplate, values: Record<string, string>): string {
  let content = template.contentText || '';
  for (const field of template.mergeFields || []) {
    const token = new RegExp(`{{\\s*${field}\\s*}}`, 'g');
    content = content.replace(token, values[field] || `[${field} requires human completion]`);
  }
  return content;
}

function pdfEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function wrapPdfLines(text: string, maxLength = 88): string[] {
  return text
    .replace(/\r/g, '')
    .split('\n')
    .flatMap((line) => {
      if (!line) return [''];
      const words = line.split(' ');
      const lines: string[] = [];
      let current = '';
      for (const word of words) {
        const next = current ? `${current} ${word}` : word;
        if (next.length > maxLength) {
          lines.push(current);
          current = word;
        } else {
          current = next;
        }
      }
      if (current) lines.push(current);
      return lines;
    });
}

function documentNeedsSignature(documentItem: DocumentRecord): boolean {
  if (typeof documentItem.requiresSignature === 'boolean') return documentItem.requiresSignature;
  const permissions = documentItem.permissions || [];
  const status = String(documentItem.status || '').toUpperCase();
  const type = `${documentItem.category || ''} ${documentItem.documentType || ''} ${documentItem.title || ''}`.toLowerCase();
  return Boolean(
    documentItem.requiresSignature ||
      permissions.includes('sign') ||
      status.includes('SIGNATURE') ||
      /contract|agreement|consent|employment|medication|gdpr|policy|safeguarding|care plan|legal/i.test(type)
  );
}

function buildSignatureCertificateId(documentId: string, signerName: string, signedAt: string): string {
  const source = `${documentId}:${signerName}:${signedAt}`;
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
  }
  return `FHC-DS-${Math.abs(hash).toString(16).toUpperCase().padStart(8, '0')}`;
}

function buildSignatureBlock(documentItem: DocumentRecord, documentSignatures: DocumentSignature[]): string {
  const requiresSignature = documentNeedsSignature(documentItem);
  const matchingSignatures = documentSignatures
    .filter((signature) => signature.documentId === documentItem.id)
    .sort((left, right) => new Date(right.signedAt || 0).getTime() - new Date(left.signedAt || 0).getTime());

  if (!requiresSignature && matchingSignatures.length === 0) return '';

  const lines = [
    '',
    'DIGITAL SIGNATURE',
    `Signature required: ${requiresSignature ? 'Yes' : 'No'}`,
    `Signature status: ${matchingSignatures.length ? 'SIGNED' : 'PENDING HUMAN SIGNATURE'}`,
  ];

  if (matchingSignatures.length === 0) {
    lines.push('No digital signature has been saved for this document yet.');
    return lines.join('\n');
  }

  matchingSignatures.forEach((signature, index) => {
    const signedAt = signature.signedAt || new Date().toISOString();
    const certificateId = signature.certificateId || buildSignatureCertificateId(documentItem.id, signature.signerName, signedAt);
    lines.push(
      '',
      `Signature ${index + 1}`,
      `Signed by: ${signature.signerName}`,
      `Role: ${signature.signerRole}`,
      `Signed at: ${new Date(signedAt).toLocaleString('en-GB')}`,
      `Certificate ID: ${certificateId}`,
      `Signature method: ${signature.signatureMethod === 'DRAWN_ESIGN' ? 'Drawn e-signature' : 'Digital certificate'}`,
      `Signature text: ${signature.signatureText || `${signature.signerName} - digitally signed`}`
    );
    if (signature.signatureImageDataUrl) {
      lines.push('Drawn signature image: captured and stored with the encrypted document signature record.');
    }
  });

  lines.push('', 'This digital signature record is stored in the encrypted server document database and audit log.');
  return lines.join('\n');
}

function buildDocumentPdfBody(documentItem: DocumentRecord, body: string, documentSignatures: DocumentSignature[]): string {
  return [
    `Document ID: ${documentItem.id}`,
    `Category: ${documentItem.category}`,
    `Type: ${documentItem.documentType || 'Document'}`,
    `Owner: ${documentItem.ownerName || documentItem.ownerType || 'Company'}`,
    `Status: ${documentItem.status || 'ACTIVE'}`,
    `Version: ${documentItem.currentVersion || 1}`,
    `Expiry: ${formatDate(documentItem.expiryDate)}`,
    buildSignatureBlock(documentItem, documentSignatures),
    '',
    'DOCUMENT CONTENT',
    body || 'No text content was stored for this file.',
  ].filter(Boolean).join('\n');
}

function buildPdfBlob(title: string, body: string): Blob {
  const lines = wrapPdfLines(`${title}\n\n${body || 'No text content was stored for this file.'}`);
  const commands = ['BT', '/F1 14 Tf', '50 790 Td', '18 TL'];
  lines.slice(0, 44).forEach((line, index) => {
    commands.push(`${index === 0 ? '' : 'T* '}(${pdfEscape(line)}) Tj`);
  });
  commands.push('ET');
  const stream = commands.join('\n');
  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj',
    '4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj',
    `5 0 obj << /Length ${stream.length} >> stream\n${stream}\nendstream endobj`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  for (const object of objects) {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header = '', base64 = ''] = dataUrl.split(',');
  const mimeMatch = header.match(/data:([^;]+);base64/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

async function fetchEncryptedObject(version: DocumentVersion, token?: string | null): Promise<Blob> {
  const response = await fetch(`/api/document-objects/${encodeURIComponent(version.id)}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorPayload.error || 'Could not download encrypted document object');
  }
  return response.blob();
}

function textToDataUrl(text: string): string {
  return `data:text/plain;base64,${btoa(unescape(encodeURIComponent(text)))}`;
}

function formatDate(value?: string): string {
  if (!value) return 'No expiry';
  return new Date(value).toLocaleDateString('en-GB');
}

export const DocumentManagement: React.FC = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('library');
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [accessLogs, setAccessLogs] = useState<DocumentAccessLog[]>([]);
  const [signatures, setSignatures] = useState<DocumentSignature[]>([]);
  const [clients, setClients] = useState<PersonRecord[]>([]);
  const [carers, setCarers] = useState<PersonRecord[]>([]);
  const [uploadForm, setUploadForm] = useState<UploadFormState>(emptyUploadForm);
  const [templateForm, setTemplateForm] = useState<DocumentTemplate>(emptyTemplateForm);
  const [templateMergeFieldsText, setTemplateMergeFieldsText] = useState('');
  const [templateImportName, setTemplateImportName] = useState('');
  const [fillForm, setFillForm] = useState<FillFormState>({
    templateId: '',
    targetType: 'carer',
    targetId: '',
    valuesText: '',
  });
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureRole, setSignatureRole] = useState('Administrator');
  const [signatureMode, setSignatureMode] = useState<'certificate' | 'draw'>('draw');
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingSignatureRef = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [nextDocuments, nextTemplates, nextVersions, nextLogs, nextSignatures, nextClients, nextCarers] = await Promise.all([
        getCollection<DocumentRecord>('documents', token),
        getCollection<DocumentTemplate>('documentTemplates', token),
        getCollection<DocumentVersion>('documentVersions', token),
        getCollection<DocumentAccessLog>('documentAccessLogs', token),
        getCollection<DocumentSignature>('documentSignatures', token),
        getCollection<PersonRecord>('clients', token),
        getCollection<PersonRecord>('carers', token),
      ]);
      setDocuments(nextDocuments);
      setTemplates(nextTemplates);
      setVersions(nextVersions);
      setAccessLogs(nextLogs);
      setSignatures(nextSignatures);
      setClients(nextClients);
      setCarers(nextCarers);
      setFillForm((current) => ({
        ...current,
        templateId: current.templateId || nextTemplates[0]?.id || '',
        targetId: current.targetId || nextCarers[0]?.id || nextClients[0]?.id || '',
      }));
      setSelectedDocumentId((current) => current || nextDocuments[0]?.id || '');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load document module');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const prepareSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const width = canvas.clientWidth || 520;
    const height = canvas.clientHeight || 180;
    const ratio = window.devicePixelRatio || 1;
    if (canvas.width !== Math.floor(width * ratio) || canvas.height !== Math.floor(height * ratio)) {
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      const context = canvas.getContext('2d');
      if (!context) return;
      context.scale(ratio, ratio);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.strokeStyle = '#111827';
      context.lineWidth = 2.4;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      setHasDrawnSignature(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'signatures') {
      window.setTimeout(prepareSignatureCanvas, 50);
    }
  }, [activeTab, prepareSignatureCanvas]);

  const filteredDocuments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return documents.filter((documentItem) => {
      const matchesCategory = categoryFilter === 'All' || documentItem.category === categoryFilter;
      const haystack = `${documentItem.title} ${documentItem.category} ${documentItem.ownerName || ''} ${documentItem.status || ''}`.toLowerCase();
      return matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [categoryFilter, documents, query]);

  const selectedDocument = useMemo(
    () => documents.find((documentItem) => documentItem.id === selectedDocumentId) || documents[0],
    [documents, selectedDocumentId]
  );

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === fillForm.templateId) || templates[0],
    [fillForm.templateId, templates]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setTemplateForm({
      ...emptyTemplateForm,
      ...selectedTemplate,
      mergeFields: selectedTemplate.mergeFields || [],
      defaultPermissions: selectedTemplate.defaultPermissions || ['view', 'download', 'print', 'sign'],
      accessRoles: selectedTemplate.accessRoles || ['ADMIN', 'MANAGER'],
      requiresSignature: selectedTemplate.requiresSignature ?? true,
    });
    setTemplateMergeFieldsText((selectedTemplate.mergeFields || []).join(', '));
    setTemplateImportName('');
  }, [selectedTemplate]);

  const metrics = useMemo(() => {
    const today = new Date();
    const inThirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const expiring = documents.filter((documentItem) => {
      if (!documentItem.expiryDate) return false;
      const expiry = new Date(documentItem.expiryDate);
      return expiry >= today && expiry <= inThirtyDays;
    }).length;
    const signedIds = new Set(signatures.map((signature) => signature.documentId));
    return {
      total: documents.length,
      signed: signedIds.size,
      expiring,
      restricted: documents.filter((documentItem) => (documentItem.accessRoles || []).length <= 2).length,
    };
  }, [documents, signatures]);

  const logAccess = useCallback(async (documentId: string, action: string, details = '') => {
    const role = user?.roles?.[0] || 'USER';
    const actor = user?.email || user?.name || 'web-user';
    const log = await saveCollectionRecord<DocumentAccessLog>('documentAccessLogs', {
      documentId,
      action,
      actor,
      role,
      details,
      at: new Date().toISOString(),
    }, token);
    setAccessLogs((current) => [log, ...current]);
  }, [token, user]);

  const handleDownloadPdf = async (documentItem: DocumentRecord) => {
    await logAccess(documentItem.id, 'download', 'Downloaded document');
    const latestVersion = versions
      .filter((version) => version.documentId === documentItem.id)
      .sort((a, b) => b.version - a.version)[0];
    const documentSignatures = signatures.filter((signature) => signature.documentId === documentItem.id);
    const needsCertifiedPdf = documentNeedsSignature(documentItem) || documentSignatures.length > 0;
    if (latestVersion?.objectKey) {
      const blob = await fetchEncryptedObject(latestVersion, token);
      if ((latestVersion.mimeType || '').startsWith('text/')) {
        const body = buildDocumentPdfBody(documentItem, await blob.text(), documentSignatures);
        downloadBlob(buildPdfBlob(documentItem.title, body), `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'document'}.pdf`);
        return;
      }
      if (needsCertifiedPdf) {
        const body = buildDocumentPdfBody(
          documentItem,
          latestVersion.contentText || `Original file: ${latestVersion.fileName || documentItem.title}\nFile type: ${latestVersion.mimeType || 'unknown'}\nThe original binary file remains encrypted in server object storage.`,
          documentSignatures
        );
        downloadBlob(buildPdfBlob(`${documentItem.title} - signed certificate`, body), `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'document'}-signed.pdf`);
        return;
      }
      downloadBlob(blob, latestVersion.fileName || `${documentItem.title}.pdf`);
      return;
    }
    if (latestVersion?.fileDataUrl && latestVersion.mimeType === 'application/pdf') {
      if (needsCertifiedPdf) {
        const body = buildDocumentPdfBody(
          documentItem,
          latestVersion.contentText || `Original PDF file: ${latestVersion.fileName || documentItem.title}\nThe original PDF is stored with this document record.`,
          documentSignatures
        );
        downloadBlob(buildPdfBlob(`${documentItem.title} - signed certificate`, body), `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'document'}-signed.pdf`);
        return;
      }
      downloadBlob(dataUrlToBlob(latestVersion.fileDataUrl), latestVersion.fileName || `${documentItem.title}.pdf`);
      return;
    }
    const blob = buildPdfBlob(documentItem.title, buildDocumentPdfBody(documentItem, latestVersion?.contentText || documentItem.contentText || '', documentSignatures));
    downloadBlob(blob, `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'document'}.pdf`);
  };

  const handlePrintPdf = async (documentItem: DocumentRecord) => {
    await logAccess(documentItem.id, 'print', 'Opened document PDF for printing');
    const latestVersion = versions
      .filter((version) => version.documentId === documentItem.id)
      .sort((a, b) => b.version - a.version)[0];
    const documentSignatures = signatures.filter((signature) => signature.documentId === documentItem.id);
    const needsCertifiedPdf = documentNeedsSignature(documentItem) || documentSignatures.length > 0;
    if (latestVersion?.objectKey) {
      const blob = await fetchEncryptedObject(latestVersion, token);
      if ((latestVersion.mimeType || '').startsWith('text/')) {
        const pdfBlob = buildPdfBlob(documentItem.title, buildDocumentPdfBody(documentItem, await blob.text(), documentSignatures));
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const pdfPopup = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        if (!pdfPopup) toast.error('The browser blocked the print window.');
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 15_000);
        return;
      }
      if (needsCertifiedPdf) {
        const pdfBlob = buildPdfBlob(
          `${documentItem.title} - signed certificate`,
          buildDocumentPdfBody(
            documentItem,
            latestVersion.contentText || `Original file: ${latestVersion.fileName || documentItem.title}\nFile type: ${latestVersion.mimeType || 'unknown'}\nThe original binary file remains encrypted in server object storage.`,
            documentSignatures
          )
        );
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const pdfPopup = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        if (!pdfPopup) toast.error('The browser blocked the print window.');
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 15_000);
        return;
      }
      const url = URL.createObjectURL(blob);
      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (!popup) toast.error('The browser blocked the print window.');
      setTimeout(() => URL.revokeObjectURL(url), 15_000);
      return;
    }
    if (latestVersion?.fileDataUrl && latestVersion.mimeType === 'application/pdf') {
      if (needsCertifiedPdf) {
        const pdfBlob = buildPdfBlob(
          `${documentItem.title} - signed certificate`,
          buildDocumentPdfBody(
            documentItem,
            latestVersion.contentText || `Original PDF file: ${latestVersion.fileName || documentItem.title}\nThe original PDF is stored with this document record.`,
            documentSignatures
          )
        );
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const pdfPopup = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        if (!pdfPopup) toast.error('The browser blocked the print window.');
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 15_000);
        return;
      }
      const url = URL.createObjectURL(dataUrlToBlob(latestVersion.fileDataUrl));
      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (!popup) toast.error('The browser blocked the print window.');
      setTimeout(() => URL.revokeObjectURL(url), 15_000);
      return;
    }
    const blob = buildPdfBlob(documentItem.title, buildDocumentPdfBody(documentItem, latestVersion?.contentText || documentItem.contentText || '', documentSignatures));
    const url = URL.createObjectURL(blob);
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) toast.error('The browser blocked the print window.');
    setTimeout(() => URL.revokeObjectURL(url), 15_000);
  };

  const toggleListValue = (value: string, current: string[], updater: (next: string[]) => void) => {
    updater(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isTextLike = file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md') || file.name.endsWith('.csv');
    const fileDataUrl = await new Promise<string>((resolvePromise, rejectPromise) => {
      const reader = new FileReader();
      reader.onload = () => resolvePromise(String(reader.result || ''));
      reader.onerror = () => rejectPromise(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
    const contentText = isTextLike
      ? await file.text()
      : `Uploaded file: ${file.name}\nType: ${file.type || 'unknown'}\nSize: ${file.size} bytes\n\nThe original binary file is saved in encrypted server object storage. This text is retained only as searchable metadata and print fallback.`;
    setUploadForm((current) => ({
      ...current,
      title: current.title || file.name.replace(/\.[^.]+$/, ''),
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      fileDataUrl,
      contentText,
    }));
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim()) {
      toast.error('Add a document title first.');
      return;
    }
    setSaving(true);
    try {
      const documentItem = await saveCollectionRecord<DocumentRecord>('documents', {
        title: uploadForm.title.trim(),
        category: uploadForm.category,
        documentType: uploadForm.documentType,
        ownerType: uploadForm.ownerType,
        ownerName: uploadForm.ownerName,
        expiryDate: uploadForm.expiryDate,
        currentVersion: 1,
        status: 'ACTIVE',
        accessRoles: uploadForm.accessRoles,
        permissions: uploadForm.permissions,
        requiresSignature: uploadForm.requiresSignature || uploadForm.permissions.includes('sign'),
        contentText: uploadForm.contentText,
      }, token);
      const version = await saveCollectionRecord<DocumentVersion>('documentVersions', {
        documentId: documentItem.id,
        version: 1,
        fileName: uploadForm.fileName || `${documentItem.title}.txt`,
        mimeType: uploadForm.mimeType,
        sizeBytes: uploadForm.sizeBytes,
        contentText: uploadForm.contentText,
        fileDataUrl: uploadForm.fileDataUrl,
        createdBy: user?.email || 'admin',
      }, token);
      await logAccess(documentItem.id, 'upload', `Uploaded ${version.fileName || documentItem.title}`);
      setDocuments((current) => [documentItem, ...current]);
      setVersions((current) => [version, ...current]);
      setUploadForm(emptyUploadForm);
      setSelectedDocumentId(documentItem.id);
      setActiveTab('library');
      toast.success('Document saved. The file object is encrypted on the server.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not upload document');
    } finally {
      setSaving(false);
    }
  };

  const handleNewTemplate = () => {
    setTemplateForm(emptyTemplateForm);
    setTemplateMergeFieldsText('');
    setTemplateImportName('');
    setFillForm((current) => ({ ...current, templateId: '' }));
  };

  const handleTemplateContentChange = (contentText: string) => {
    setTemplateForm((current) => ({ ...current, contentText }));
    const detectedFields = parseMergeFieldsFromContent(contentText);
    if (detectedFields.length > 0) {
      setTemplateMergeFieldsText(detectedFields.join(', '));
    }
  };

  const handleTemplateImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isEditableText =
      file.type.startsWith('text/') ||
      /\.(txt|md|csv|html|htm)$/i.test(file.name);
    const contentText = isEditableText
      ? await file.text()
      : [
          `Imported company contract file: ${file.name}`,
          `File type: ${file.type || 'unknown'}`,
          `File size: ${file.size} bytes`,
          '',
          'Paste the editable contract wording here before saving this as an application template.',
          'Use merge fields like {{employeeName}}, {{clientName}}, {{startDate}}, {{hourlyRate}} where the app should fill data automatically.',
        ].join('\n');
    const detectedFields = parseMergeFieldsFromContent(contentText);
    setTemplateImportName(file.name);
    setTemplateForm((current) => ({
      ...current,
      id: current.id || '',
      title: current.title || file.name.replace(/\.[^.]+$/, ''),
      description: current.description || `Imported from company file ${file.name}. Editable template copy for controlled document generation.`,
      contentText,
      mergeFields: detectedFields,
    }));
    setTemplateMergeFieldsText(detectedFields.join(', '));
    toast.success(isEditableText ? 'Contract text imported as editable template content.' : 'File imported. Paste or edit the contract wording before saving.');
  };

  const handleSaveTemplate = async () => {
    const title = templateForm.title.trim();
    if (!title) {
      toast.error('Add a template title first.');
      return;
    }
    const contentText = templateForm.contentText || '';
    const mergeFields = parseMergeFieldsText(templateMergeFieldsText);
    const detectedFields = parseMergeFieldsFromContent(contentText);
    const finalMergeFields = mergeFields.length ? mergeFields : detectedFields;
    const id = templateForm.id || templateIdFromTitle(title);
    setSaving(true);
    try {
      const savedTemplate = await saveCollectionRecord<DocumentTemplate>('documentTemplates', {
        ...templateForm,
        id,
        title,
        category: templateForm.category || 'General',
        description: templateForm.description || '',
        contentText,
        mergeFields: finalMergeFields,
        defaultPermissions: templateForm.defaultPermissions?.length ? templateForm.defaultPermissions : ['view', 'download', 'print'],
        accessRoles: templateForm.accessRoles?.length ? templateForm.accessRoles : ['ADMIN'],
        requiresSignature: Boolean(templateForm.requiresSignature || templateForm.defaultPermissions?.includes('sign')),
      }, token, templateForm.id || undefined);
      setTemplates((current) => {
        const exists = current.some((template) => template.id === savedTemplate.id);
        return exists
          ? current.map((template) => template.id === savedTemplate.id ? savedTemplate : template)
          : [savedTemplate, ...current];
      });
      setFillForm((current) => ({ ...current, templateId: savedTemplate.id }));
      setTemplateForm(savedTemplate);
      setTemplateMergeFieldsText((savedTemplate.mergeFields || []).join(', '));
      setTemplateImportName('');
      toast.success('Template saved and ready to use.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFromTemplate = async () => {
    const template = templates.find((item) => item.id === fillForm.templateId);
    if (!template) {
      toast.error('Choose a template first.');
      return;
    }
    setSaving(true);
    try {
      const target = fillForm.targetType === 'carer'
        ? carers.find((carer) => carer.id === fillForm.targetId)
        : clients.find((client) => client.id === fillForm.targetId);
      const autoValues: Record<string, string> = {
        employeeName: displayName(target),
        carerName: displayName(target),
        clientName: displayName(target),
        roleTitle: target?.role || 'Care Assistant',
        carePackage: target?.carePackage || 'Personal care',
        hourlyRate: '',
        startDate: new Date().toLocaleDateString('en-GB'),
        reviewDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB'),
        managerName: user?.name || user?.email || 'Administrator',
      };
      const values = { ...autoValues, ...parseValuesText(fillForm.valuesText) };
      const rate = target?.hourlyRate || target?.defaultHourlyRate;
      if (rate) {
        values.hourlyRate = `£${rate}`;
      }
      const contentText = fillTemplate(template, values);
      const documentItem = await saveCollectionRecord<DocumentRecord>('documents', {
        title: `${template.title} - ${displayName(target) || 'Manual draft'}`,
        category: template.category,
        documentType: 'AI completed template',
        ownerType: fillForm.targetType === 'manual' ? 'Manual' : fillForm.targetType,
        ownerName: displayName(target),
        status: 'DRAFT_READY_FOR_SIGNATURE',
        currentVersion: 1,
        accessRoles: template.accessRoles || ['ADMIN', 'MANAGER'],
        permissions: template.defaultPermissions || ['view', 'download', 'print', 'sign'],
        requiresSignature: template.requiresSignature ?? ((template.defaultPermissions || []).includes('sign') || /contract|agreement|consent|medication|gdpr/i.test(`${template.category} ${template.title}`)),
        contentText,
      }, token);
      const version = await saveCollectionRecord<DocumentVersion>('documentVersions', {
        documentId: documentItem.id,
        version: 1,
        fileName: `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.txt`,
        mimeType: 'text/plain',
        sizeBytes: contentText.length,
        contentText,
        fileDataUrl: textToDataUrl(contentText),
        createdBy: user?.email || 'admin',
      }, token);
      await logAccess(documentItem.id, 'ai-template-fill', 'Created from controlled template with human approval required');
      setDocuments((current) => [documentItem, ...current]);
      setVersions((current) => [version, ...current]);
      setSelectedDocumentId(documentItem.id);
      setActiveTab('signatures');
      toast.success('AI-style draft created. Human review and signature are still required.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create template document');
    } finally {
      setSaving(false);
    }
  };

  const handleNewVersion = async (documentItem: DocumentRecord) => {
    const contentText = window.prompt('Paste the corrected document text for the new version:', documentItem.contentText || '');
    if (contentText === null) return;
    setSaving(true);
    try {
      const nextVersionNumber = Number(documentItem.currentVersion || 1) + 1;
      const version = await saveCollectionRecord<DocumentVersion>('documentVersions', {
        documentId: documentItem.id,
        version: nextVersionNumber,
        fileName: `${documentItem.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-v${nextVersionNumber}.txt`,
        mimeType: 'text/plain',
        sizeBytes: contentText.length,
        contentText,
        fileDataUrl: textToDataUrl(contentText),
        createdBy: user?.email || 'admin',
      }, token);
      const updatedDocument = await saveCollectionRecord<DocumentRecord>('documents', {
        ...documentItem,
        currentVersion: nextVersionNumber,
        contentText,
        status: documentNeedsSignature(documentItem) ? 'DRAFT_READY_FOR_SIGNATURE' : 'ACTIVE',
        signedAt: '',
        signedBy: '',
      }, token, documentItem.id);
      await logAccess(documentItem.id, 'new-version', `Created version ${nextVersionNumber}`);
      setVersions((current) => [version, ...current]);
      setDocuments((current) => current.map((item) => item.id === updatedDocument.id ? updatedDocument : item));
      toast.success('New version saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save new version');
    } finally {
      setSaving(false);
    }
  };

  const getSignatureCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startDrawnSignature = (event: React.PointerEvent<HTMLCanvasElement>) => {
    prepareSignatureCanvas();
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    canvas.setPointerCapture(event.pointerId);
    const point = getSignatureCanvasPoint(event);
    isDrawingSignatureRef.current = true;
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const continueDrawnSignature = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingSignatureRef.current) return;
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!context) return;
    const point = getSignatureCanvasPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
    setHasDrawnSignature(true);
  };

  const endDrawnSignature = (event?: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (event && canvas?.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    isDrawingSignatureRef.current = false;
  };

  const clearDrawnSignature = () => {
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const rect = canvas.getBoundingClientRect();
    context.clearRect(0, 0, rect.width, rect.height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.strokeStyle = '#111827';
    context.lineWidth = 2.4;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    setHasDrawnSignature(false);
  };

  const getDrawnSignatureDataUrl = () => {
    if (signatureMode !== 'draw' || !hasDrawnSignature) return '';
    return signatureCanvasRef.current?.toDataURL('image/png') || '';
  };

  const handleSign = async () => {
    if (!selectedDocument) {
      toast.error('Choose a document first.');
      return;
    }
    if (!signatureName.trim()) {
      toast.error('Add signer name.');
      return;
    }
    const drawnSignatureDataUrl = getDrawnSignatureDataUrl();
    if (signatureMode === 'draw' && !drawnSignatureDataUrl) {
      toast.error('Draw the e-signature before saving, or switch to certificate only.');
      return;
    }
    setSaving(true);
    try {
      const signedAt = new Date().toISOString();
      const certificateId = buildSignatureCertificateId(selectedDocument.id, signatureName.trim(), signedAt);
      const signature = await saveCollectionRecord<DocumentSignature>('documentSignatures', {
        documentId: selectedDocument.id,
        signerName: signatureName.trim(),
        signerRole: signatureRole,
        signatureText: signatureMode === 'draw'
          ? `${signatureName.trim()} - drawn e-signature - ${certificateId}`
          : `${signatureName.trim()} - digitally signed - ${certificateId}`,
        certificateId,
        signatureMethod: signatureMode === 'draw' ? 'DRAWN_ESIGN' : 'DIGITAL_CERTIFICATE',
        signatureImageDataUrl: drawnSignatureDataUrl,
        signedAt,
        status: 'SIGNED',
      }, token);
      const updatedDocument = await saveCollectionRecord<DocumentRecord>('documents', {
        ...selectedDocument,
        status: 'SIGNED',
        requiresSignature: true,
        signedAt,
        signedBy: signatureName.trim(),
      }, token, selectedDocument.id);
      await logAccess(selectedDocument.id, 'digital-signature', `Signed by ${signature.signerName}`);
      setSignatures((current) => [signature, ...current]);
      setDocuments((current) => current.map((item) => item.id === updatedDocument.id ? updatedDocument : item));
      setSignatureName('');
      if (signatureMode === 'draw') clearDrawnSignature();
      toast.success('Digital signature saved in the document database.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign document');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (documentItem: DocumentRecord) => {
    setSaving(true);
    try {
      const updated = await saveCollectionRecord<DocumentRecord>('documents', {
        ...documentItem,
        status: 'ARCHIVED',
      }, token, documentItem.id);
      await logAccess(documentItem.id, 'archive', 'Document archived');
      setDocuments((current) => current.map((item) => item.id === updated.id ? updated : item));
      toast.success('Document archived.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not archive document');
    } finally {
      setSaving(false);
    }
  };

  const handleExportAuditPack = async (format: 'json' | 'pdf') => {
    const pack = {
      exportedAt: new Date().toISOString(),
      exportedBy: user?.email || user?.name || 'admin',
      documents,
      versions,
      signatures,
      accessLogs,
    };
    if (format === 'json') {
      downloadBlob(new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' }), 'fuchsia-document-audit-pack.json');
      return;
    }
    const summary = [
      `Exported by: ${pack.exportedBy}`,
      `Exported at: ${new Date(pack.exportedAt).toLocaleString('en-GB')}`,
      '',
      `Documents: ${documents.length}`,
      `Versions: ${versions.length}`,
      `Digital signatures: ${signatures.length}`,
      `Access log entries: ${accessLogs.length}`,
      '',
      ...documents.map((documentItem) => `${documentItem.title} | ${documentItem.category} | ${documentItem.status || 'ACTIVE'} | v${documentItem.currentVersion || 1}`),
    ].join('\n');
    downloadBlob(buildPdfBlob('Fuchsia Homecare Document Audit Pack', summary), 'fuchsia-document-audit-pack.pdf');
  };

  const handlePermissionsSave = async (documentItem: DocumentRecord, key: 'accessRoles' | 'permissions', value: string) => {
    const current = documentItem[key] || [];
    const nextValues = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
    const updated = await saveCollectionRecord<DocumentRecord>('documents', {
      ...documentItem,
      [key]: nextValues,
      ...(key === 'permissions' && value === 'sign' ? {
        requiresSignature: nextValues.includes('sign'),
        status: nextValues.includes('sign') && String(documentItem.status || '').toUpperCase() !== 'SIGNED'
          ? 'DRAFT_READY_FOR_SIGNATURE'
          : documentItem.status,
      } : {}),
    }, token, documentItem.id);
    await logAccess(documentItem.id, 'permissions-updated', `${key} changed`);
    setDocuments((items) => items.map((item) => item.id === updated.id ? updated : item));
  };

  const handleSignatureRequirementSave = async (documentItem: DocumentRecord) => {
    const nextRequiresSignature = !documentNeedsSignature(documentItem);
    const nextPermissions = nextRequiresSignature && !(documentItem.permissions || []).includes('sign')
      ? [...(documentItem.permissions || []), 'sign']
      : nextRequiresSignature
        ? documentItem.permissions || []
        : (documentItem.permissions || []).filter((permission) => permission !== 'sign');
    const updated = await saveCollectionRecord<DocumentRecord>('documents', {
      ...documentItem,
      requiresSignature: nextRequiresSignature,
      permissions: nextPermissions,
      status: nextRequiresSignature && String(documentItem.status || '').toUpperCase() !== 'SIGNED'
        ? 'DRAFT_READY_FOR_SIGNATURE'
        : documentItem.status,
    }, token, documentItem.id);
    await logAccess(documentItem.id, 'signature-requirement-updated', nextRequiresSignature ? 'Signature required' : 'Signature not required');
    setDocuments((items) => items.map((item) => item.id === updated.id ? updated : item));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-purple-700">
                <FileArchive className="h-5 w-5" />
                Professional Document Management
              </div>
              <h1 className="mt-2 text-3xl font-bold text-slate-950">Documents, templates, signatures and audit packs</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Upload documents, keep versions, control permissions, complete templates, collect signatures and export a full audit trail.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void loadData()}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Documents', metrics.total, FileText],
              ['Signed', metrics.signed, FileSignature],
              ['Expiring 30 days', metrics.expiring, History],
              ['Restricted', metrics.restricted, LockKeyhole],
            ].map(([label, value, Icon]) => {
              const MetricIcon = Icon as React.ElementType;
              return (
                <div key={String(label)} className="rounded-md border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">{String(label)}</span>
                    <MetricIcon className="h-5 w-5 text-purple-700" />
                  </div>
                  <div className="mt-3 text-3xl font-bold text-slate-950">{String(value)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-wrap gap-2">
          {[
            ['library', 'Library', FileText],
            ['upload', 'Upload', Upload],
            ['templates', 'Templates & AI Fill', Sparkles],
            ['signatures', 'Signatures', PenLine],
            ['logs', 'Access Log', Eye],
            ['audit', 'Audit Pack', FileArchive],
            ['permissions', 'Permissions', ShieldCheck],
          ].map(([key, label, Icon]) => {
            const TabIcon = Icon as React.ElementType;
            return (
              <button
                key={String(key)}
                type="button"
                onClick={() => setActiveTab(key as TabKey)}
                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
                  activeTab === key ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {String(label)}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-slate-600">Loading document database...</div>
        ) : null}

        {activeTab === 'library' && !loading ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_220px]">
              <label className="relative block">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search documents by title, owner, category or status"
                  className="w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm"
                />
              </label>
              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option>All</option>
                {categories.map((category) => <option key={category}>{category}</option>)}
              </select>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              {filteredDocuments.map((documentItem) => {
                const needsSignature = documentNeedsSignature(documentItem);
                const documentSignatures = signatures.filter((signature) => signature.documentId === documentItem.id);
                const isSigned = documentSignatures.length > 0 || String(documentItem.status || '').toUpperCase() === 'SIGNED';

                return (
                  <div key={documentItem.id} className="rounded-md border border-slate-200 bg-white p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="text-lg font-bold text-slate-950">{documentItem.title}</h2>
                          <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">{documentItem.category}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{documentItem.status || 'ACTIVE'}</span>
                          {needsSignature ? (
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${isSigned ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                              {isSigned ? 'Digitally signed' : 'Signature required'}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          {documentItem.documentType || 'Document'} | Owner: {documentItem.ownerName || documentItem.ownerType || 'Company'} | Version {documentItem.currentVersion || 1} | Expiry: {formatDate(documentItem.expiryDate)}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                          Roles: {(documentItem.accessRoles || []).join(', ') || 'No roles set'} | Permissions: {(documentItem.permissions || []).join(', ') || 'No permissions set'}
                        </p>
                        {documentSignatures[0] ? (
                          <p className="mt-2 text-xs font-semibold text-emerald-700">
                            Last signature: {documentSignatures[0].signerName} | {documentSignatures[0].certificateId || 'certificate saved'} | {formatDate(documentSignatures[0].signedAt)}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs font-semibold text-emerald-700">
                          File storage: encrypted server object storage for uploaded and generated document versions
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => void handleDownloadPdf(documentItem)} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-800">
                        <Download className="h-4 w-4" />
                        Download PDF
                      </button>
                      <button type="button" onClick={() => void handlePrintPdf(documentItem)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                        <Printer className="h-4 w-4" />
                        Print PDF
                      </button>
                      {needsSignature && !isSigned ? (
                        <button type="button" onClick={() => { setSelectedDocumentId(documentItem.id); setActiveTab('signatures'); }} className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100">
                          <PenLine className="h-4 w-4" />
                          Sign
                        </button>
                      ) : null}
                      <button type="button" onClick={() => void handleNewVersion(documentItem)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                        <History className="h-4 w-4" />
                        New Version
                      </button>
                      <button type="button" onClick={() => void handleArchive(documentItem)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                        <Archive className="h-4 w-4" />
                        Archive
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {activeTab === 'upload' ? (
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <h2 className="text-xl font-bold text-slate-950">Upload a document or template file</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <input value={uploadForm.title} onChange={(event) => setUploadForm((current) => ({ ...current, title: event.target.value }))} placeholder="Document title" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <select value={uploadForm.category} onChange={(event) => setUploadForm((current) => ({ ...current, category: event.target.value }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                {categories.map((category) => <option key={category}>{category}</option>)}
              </select>
              <input value={uploadForm.documentType} onChange={(event) => setUploadForm((current) => ({ ...current, documentType: event.target.value }))} placeholder="Document type" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <input value={uploadForm.ownerName} onChange={(event) => setUploadForm((current) => ({ ...current, ownerName: event.target.value }))} placeholder="Owner/client/carer name" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <input value={uploadForm.expiryDate} onChange={(event) => setUploadForm((current) => ({ ...current, expiryDate: event.target.value }))} type="date" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <input type="file" onChange={(event) => void handleFileChange(event)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <label className="mt-4 flex items-start gap-3 rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-950">
              <input
                type="checkbox"
                checked={uploadForm.requiresSignature}
                onChange={(event) => setUploadForm((current) => ({
                  ...current,
                  requiresSignature: event.target.checked,
                  permissions: event.target.checked && !current.permissions.includes('sign')
                    ? [...current.permissions, 'sign']
                    : current.permissions,
                }))}
                className="mt-1 h-4 w-4 accent-purple-700"
              />
              <span>
                <span className="block font-semibold">Requires digital signature</span>
                <span className="mt-1 block text-purple-800">Use this for contracts, consent forms, care agreements, medication/GDPR records and policies that need human approval.</span>
              </span>
            </label>
            <textarea value={uploadForm.contentText} onChange={(event) => setUploadForm((current) => ({ ...current, contentText: event.target.value }))} rows={10} placeholder="Document text, template text, contract body or notes" className="mt-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="font-semibold text-slate-900">Allowed roles</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {roleOptions.map((role) => (
                    <button key={role} type="button" onClick={() => toggleListValue(role, uploadForm.accessRoles, (next) => setUploadForm((current) => ({ ...current, accessRoles: next })))} className={`rounded-md px-3 py-2 text-xs font-semibold ${uploadForm.accessRoles.includes(role) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                      {role}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Permissions</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {permissionOptions.map((permission) => (
                    <button key={permission} type="button" onClick={() => toggleListValue(permission, uploadForm.permissions, (next) => setUploadForm((current) => ({ ...current, permissions: next })))} className={`rounded-md px-3 py-2 text-xs font-semibold ${uploadForm.permissions.includes(permission) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                      {permission}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button type="button" disabled={saving} onClick={() => void handleUpload()} className="mt-5 inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
              <Save className="h-4 w-4" />
              Save document
            </button>
          </div>
        ) : null}

        {activeTab === 'templates' ? (
          <div className="space-y-5">
            <div className="grid gap-5 xl:grid-cols-[0.85fr_1.35fr]">
              <div className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-slate-950">Editable contract templates</h2>
                    <p className="mt-1 text-sm text-slate-600">Select a template to view and edit it, or import an existing company contract as a reusable template.</p>
                  </div>
                  <button type="button" onClick={handleNewTemplate} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                    <FileText className="h-4 w-4" />
                    New template
                  </button>
                </div>
                <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-purple-300 bg-purple-50 px-4 py-3 text-sm font-semibold text-purple-800 hover:bg-purple-100">
                  <Upload className="h-4 w-4" />
                  Upload existing company contract
                  <input type="file" accept=".txt,.md,.html,.htm,.csv,.pdf,.doc,.docx" onChange={(event) => void handleTemplateImport(event)} className="hidden" />
                </label>
                {templateImportName ? (
                  <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                    Imported: {templateImportName}. Review and save the editable template content.
                  </div>
                ) : null}
                <div className="mt-4 max-h-[680px] space-y-3 overflow-y-auto pr-1">
                  {templates.map((template) => (
                    <button key={template.id} type="button" onClick={() => setFillForm((current) => ({ ...current, templateId: template.id }))} className={`w-full rounded-md border p-4 text-left ${fillForm.templateId === template.id ? 'border-purple-600 bg-purple-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-950">{template.title}</div>
                          <div className="mt-1 text-sm text-slate-600">{template.description || 'No description saved yet.'}</div>
                        </div>
                        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{template.category}</span>
                      </div>
                      <div className="mt-2 text-xs font-medium text-purple-700">{(template.mergeFields || []).join(', ') || 'No merge fields'}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-slate-950">Template editor</h2>
                    <p className="mt-1 text-sm text-slate-600">Use merge fields like {'{{clientName}}'} or {'{{employeeName}}'}. These fields are filled when creating a document draft.</p>
                  </div>
                  <button type="button" disabled={saving} onClick={() => void handleSaveTemplate()} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                    <Save className="h-4 w-4" />
                    Save template
                  </button>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <input value={templateForm.title} onChange={(event) => setTemplateForm((current) => ({ ...current, title: event.target.value }))} placeholder="Template title" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <select value={templateForm.category} onChange={(event) => setTemplateForm((current) => ({ ...current, category: event.target.value }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                    {categories.map((category) => <option key={category}>{category}</option>)}
                  </select>
                </div>
                <textarea value={templateForm.description || ''} onChange={(event) => setTemplateForm((current) => ({ ...current, description: event.target.value }))} rows={2} placeholder="Template description and intended use" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                <textarea value={templateMergeFieldsText} onChange={(event) => setTemplateMergeFieldsText(event.target.value)} rows={2} placeholder="Merge fields, separated by commas: clientName, startDate, weeklyHours" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                <textarea value={templateForm.contentText || ''} onChange={(event) => handleTemplateContentChange(event.target.value)} rows={18} placeholder={'Contract template wording:\n\nEMPLOYMENT CONTRACT\nEmployee: {{employeeName}}\nRole: {{roleTitle}}\nStart date: {{startDate}}\nHourly rate: {{hourlyRate}}'} className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm leading-6" />

                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                  <label className="flex items-start gap-3 rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-950">
                    <input
                      type="checkbox"
                      checked={Boolean(templateForm.requiresSignature)}
                      onChange={(event) => setTemplateForm((current) => ({
                        ...current,
                        requiresSignature: event.target.checked,
                        defaultPermissions: event.target.checked && !(current.defaultPermissions || []).includes('sign')
                          ? [...(current.defaultPermissions || []), 'sign']
                          : current.defaultPermissions || [],
                      }))}
                      className="mt-1 h-4 w-4 accent-purple-700"
                    />
                    <span>
                      <span className="block font-semibold">Requires signature</span>
                      <span className="mt-1 block text-xs text-purple-800">Use for contracts, agreements and consent templates.</span>
                    </span>
                  </label>
                  <div className="lg:col-span-2">
                    <h3 className="font-semibold text-slate-900">Template roles</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {roleOptions.map((role) => (
                        <button key={role} type="button" onClick={() => toggleListValue(role, templateForm.accessRoles || [], (next) => setTemplateForm((current) => ({ ...current, accessRoles: next })))} className={`rounded-md px-3 py-2 text-xs font-semibold ${(templateForm.accessRoles || []).includes(role) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                          {role}
                        </button>
                      ))}
                    </div>
                    <h3 className="mt-4 font-semibold text-slate-900">Default permissions</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {permissionOptions.map((permission) => (
                        <button key={permission} type="button" onClick={() => toggleListValue(permission, templateForm.defaultPermissions || [], (next) => setTemplateForm((current) => ({ ...current, defaultPermissions: next, requiresSignature: permission === 'sign' ? next.includes('sign') : current.requiresSignature })))} className={`rounded-md px-3 py-2 text-xs font-semibold ${(templateForm.defaultPermissions || []).includes(permission) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                          {permission}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5">
              <h2 className="text-xl font-bold text-slate-950">AI-assisted completion</h2>
              <p className="mt-2 text-sm text-slate-600">The app fills the selected company template with database data and your extra values. Sensitive documents remain drafts until a human reviews and signs them.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <select value={fillForm.targetType} onChange={(event) => setFillForm((current) => ({ ...current, targetType: event.target.value as FillFormState['targetType'], targetId: '' }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option value="carer">Carer</option>
                  <option value="client">Client</option>
                  <option value="manual">Manual</option>
                </select>
                <select value={fillForm.targetId} onChange={(event) => setFillForm((current) => ({ ...current, targetId: event.target.value }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={fillForm.targetType === 'manual'}>
                  <option value="">Choose record</option>
                  {(fillForm.targetType === 'client' ? clients : carers).map((person) => <option key={person.id} value={person.id}>{displayName(person)}</option>)}
                </select>
              </div>
              <textarea value={fillForm.valuesText} onChange={(event) => setFillForm((current) => ({ ...current, valuesText: event.target.value }))} rows={8} placeholder={'Extra values, one per line:\nworkingHours: 37.5 per week\ngrossPay: £520.00\nnetPay: £480.00'} className="mt-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <button type="button" disabled={saving} onClick={() => void handleCreateFromTemplate()} className="mt-4 inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                <Sparkles className="h-4 w-4" />
                Create document draft
              </button>
            </div>
          </div>
        ) : null}

        {activeTab === 'signatures' ? (
          <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
            <div className="rounded-md border border-slate-200 bg-white p-5">
              <h2 className="text-xl font-bold text-slate-950">Digital signature</h2>
              {selectedDocument ? (
                <div className={`mt-3 rounded-md p-3 text-sm ${documentNeedsSignature(selectedDocument) ? 'bg-amber-50 text-amber-900' : 'bg-slate-50 text-slate-700'}`}>
                  {documentNeedsSignature(selectedDocument)
                    ? 'This document requires a digital signature. The signed certificate will be added automatically to downloaded/printed PDFs.'
                    : 'This document does not currently require a signature, but an authorised user may still add one if needed.'}
                </div>
              ) : null}
              <select value={selectedDocument?.id || ''} onChange={(event) => setSelectedDocumentId(event.target.value)} className="mt-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                {documents.map((documentItem) => <option key={documentItem.id} value={documentItem.id}>{documentItem.title}</option>)}
              </select>
              <input value={signatureName} onChange={(event) => setSignatureName(event.target.value)} placeholder="Signer full name" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <input value={signatureRole} onChange={(event) => setSignatureRole(event.target.value)} placeholder="Signer role" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-bold text-slate-950">Signature method</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSignatureMode('draw')}
                    className={`rounded-md px-3 py-2 text-sm font-semibold ${signatureMode === 'draw' ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}
                  >
                    E-sign draw
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignatureMode('certificate')}
                    className={`rounded-md px-3 py-2 text-sm font-semibold ${signatureMode === 'certificate' ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}
                  >
                    Certificate only
                  </button>
                </div>
                {signatureMode === 'draw' ? (
                  <div className="mt-4">
                    <div className="rounded-md border border-slate-300 bg-white p-2">
                      <canvas
                        ref={signatureCanvasRef}
                        onPointerDown={startDrawnSignature}
                        onPointerMove={continueDrawnSignature}
                        onPointerUp={endDrawnSignature}
                        onPointerLeave={endDrawnSignature}
                        className="h-44 w-full touch-none rounded bg-white"
                        aria-label="Draw e-signature"
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-xs text-slate-600">Draw with mouse, finger or stylus. This image is saved with the signature record.</p>
                      <button
                        type="button"
                        onClick={clearDrawnSignature}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Clear
                      </button>
                    </div>
                    {hasDrawnSignature ? (
                      <p className="mt-2 text-xs font-semibold text-emerald-700">Drawn e-signature captured.</p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-slate-600">The system will save a certificate ID, signer details and audit log without a drawn signature image.</p>
                )}
              </div>
              <div className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                Signing confirms the signer reviewed the latest document version. Human approval is required for contracts, medication, care and employment documents.
              </div>
              <button type="button" disabled={saving} onClick={() => void handleSign()} className="mt-4 inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                <PenLine className="h-4 w-4" />
                Save digital signature
              </button>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-5">
              <h2 className="text-xl font-bold text-slate-950">Signature register</h2>
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                <h3 className="text-sm font-bold text-amber-950">Documents waiting for signature</h3>
                <div className="mt-2 space-y-2">
                  {documents
                    .filter((documentItem) => documentNeedsSignature(documentItem) && !signatures.some((signature) => signature.documentId === documentItem.id) && String(documentItem.status || '').toUpperCase() !== 'SIGNED')
                    .map((documentItem) => (
                      <button
                        key={documentItem.id}
                        type="button"
                        onClick={() => setSelectedDocumentId(documentItem.id)}
                        className="block w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-left text-sm font-semibold text-amber-950 hover:bg-amber-100"
                      >
                        {documentItem.title}
                      </button>
                    ))}
                  {documents.filter((documentItem) => documentNeedsSignature(documentItem) && !signatures.some((signature) => signature.documentId === documentItem.id) && String(documentItem.status || '').toUpperCase() !== 'SIGNED').length === 0 ? (
                    <p className="text-sm text-amber-900">No required signatures are pending.</p>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {signatures.map((signature) => (
                  <div key={signature.id} className="rounded-md border border-slate-200 p-3">
                    <div className="font-semibold text-slate-950">{signature.signerName}</div>
                    <div className="text-sm text-slate-600">{signature.signerRole} | {formatDate(signature.signedAt)}</div>
                    <div className="text-xs font-semibold text-emerald-700">{signature.certificateId || 'Certificate saved'}</div>
                    <div className="text-xs text-slate-500">{signature.signatureMethod === 'DRAWN_ESIGN' ? 'Drawn e-signature' : 'Digital certificate'}</div>
                    {signature.signatureImageDataUrl ? (
                      <div className="mt-2 rounded-md border border-slate-200 bg-white p-2">
                        <img src={signature.signatureImageDataUrl} alt={`${signature.signerName} drawn signature`} className="h-16 max-w-full object-contain" />
                      </div>
                    ) : null}
                    <div className="text-xs text-slate-500">{documents.find((item) => item.id === signature.documentId)?.title || signature.documentId}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'logs' ? (
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <h2 className="text-xl font-bold text-slate-950">Real document access log</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead>
                  <tr className="text-left text-slate-600">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Document</th>
                    <th className="py-2 pr-3">Action</th>
                    <th className="py-2 pr-3">Actor</th>
                    <th className="py-2 pr-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accessLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="py-2 pr-3">{new Date(log.at || '').toLocaleString('en-GB')}</td>
                      <td className="py-2 pr-3">{documents.find((item) => item.id === log.documentId)?.title || log.documentId}</td>
                      <td className="py-2 pr-3 font-semibold text-purple-700">{log.action}</td>
                      <td className="py-2 pr-3">{log.actor}</td>
                      <td className="py-2 pr-3 text-slate-600">{log.details || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {activeTab === 'audit' ? (
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <h2 className="text-xl font-bold text-slate-950">Export audit pack</h2>
            <p className="mt-2 text-sm text-slate-600">The pack includes document metadata, version history, signatures and the access log.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => void handleExportAuditPack('pdf')} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800">
                <Download className="h-4 w-4" />
                Download PDF audit pack
              </button>
              <button type="button" onClick={() => void handleExportAuditPack('json')} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                <FileArchive className="h-4 w-4" />
                Download JSON evidence pack
              </button>
            </div>
          </div>
        ) : null}

        {activeTab === 'permissions' ? (
          <div className="space-y-4">
            {documents.map((documentItem) => (
              <div key={documentItem.id} className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2">
                  <LockKeyhole className="h-5 w-5 text-purple-700" />
                  <h2 className="text-lg font-bold text-slate-950">{documentItem.title}</h2>
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">Allowed roles</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {roleOptions.map((role) => (
                        <button key={role} type="button" onClick={() => void handlePermissionsSave(documentItem, 'accessRoles', role)} className={`rounded-md px-3 py-2 text-xs font-semibold ${(documentItem.accessRoles || []).includes(role) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                          {role}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Document permissions</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {permissionOptions.map((permission) => (
                        <button key={permission} type="button" onClick={() => void handlePermissionsSave(documentItem, 'permissions', permission)} className={`rounded-md px-3 py-2 text-xs font-semibold ${(documentItem.permissions || []).includes(permission) ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                          {permission}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleSignatureRequirementSave(documentItem)}
                      className={`mt-3 inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold ${
                        documentNeedsSignature(documentItem)
                          ? 'bg-amber-100 text-amber-900'
                          : 'border border-slate-300 bg-white text-slate-700'
                      }`}
                    >
                      <PenLine className="h-4 w-4" />
                      {documentNeedsSignature(documentItem) ? 'Requires digital signature' : 'Mark as signature required'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!loading && documents.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-slate-600">
            No documents yet. Upload the first policy, contract, payslip or consent form.
          </div>
        ) : null}

        {saving ? (
          <div className="fixed bottom-4 right-4 inline-flex items-center gap-2 rounded-md bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-lg">
            <CheckCircle className="h-4 w-4" />
            Saving to server database...
          </div>
        ) : null}
      </div>
    </div>
  );
};
